import { useId, type TextareaHTMLAttributes } from 'react';
import { cn } from './cn.ts';

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  readonly label: string;
  readonly error?: string;
  readonly hint?: string;
}

/**
 * A labelled multi-line input.
 *
 * Its own component rather than a mode of `Field`, because the two differ in
 * every attribute that matters — rows against type, no placeholder habit to
 * resist — and a component with a `as="textarea"` switch ends up typed as
 * neither element.
 */
export function TextArea({ label, error, hint, className, ...rest }: TextAreaProps) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-slate-700">
        {label}
      </label>
      <textarea
        {...rest}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          'rounded-md border px-3 py-2 text-sm text-slate-900 shadow-sm',
          'focus:outline focus:outline-2 focus:outline-offset-0',
          error
            ? 'border-red-400 focus:outline-red-500'
            : 'border-slate-300 focus:outline-blue-500',
          className,
        )}
      />
      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
